import BaseApi from './BaseApi';
import RootApiService from './RootApiService';
import { TournamentStatus } from '@/services/utils/enums';
import { makeLogger } from '@/services/utils/logger';
import { formatPublicTournament } from '@/services/utils/publicTournament';

const log = makeLogger('PublicTournamentService', false);

class PublicTournamentService extends BaseApi {
  constructor() {
    super('/tournaments');
  }

  async getUpcomingPublic(options = {}) {
    return (await this.getUpcomingEntries(options)).map(formatPublicTournament).filter(Boolean);
  }

  async getUpcomingEntries(options = {}) {
    const limit = Number.isFinite(Number(options?.limit))
      ? Math.max(1, Math.floor(Number(options.limit)))
      : 10;
    const status = options?.status || TournamentStatus.CREATED;
    const statusFilter = Array.isArray(status)
      ? { $in: status }
      : { $eq: status };
    const fromDateISO = options?.fromDateISO || this.#getTodayDateString();

    try {
      const res = await this.api.get('/', {
        params: {
          filters: {
            deleted: { $ne: true },
            ...(options.includeOngoing ? {
              $or: [
                { tournamentStatus: { $eq: TournamentStatus.ONGOING } },
                { tournamentStatus: statusFilter, date: { $gte: fromDateISO } },
              ],
            } : { tournamentStatus: statusFilter, date: { $gte: fromDateISO } }),
            ...(options.clubId ? { chess_clubs: { documentId: { $eq: options.clubId } } } : {}),
          },
          fields: [
            'name',
            'slug',
            'date',
            'time',
            'place',
            'finishedAt',
            'system',
            'competitionFormat',
            'rated',
            'chessVariant',
            'tournamentStatus',
            'participantLimit',
          ],
          populate: {
            players: {
              count: true,
            },
            chess_clubs: {
              fields: ['documentId', 'name', 'slug', 'city', 'country'],
              populate: {
                background: { fields: ['url', 'width', 'height', 'formats'] },
              },
            },
            systemSettings: {
              on: {
                'settings.mixed-settings': { populate: '*' },
                'settings.swiss-settings': { fields: ['timeMinutes', 'increment', 'controlType'] },
                'settings.elimination-settings': { fields: ['timeMinutes', 'increment', 'controlType'] },
                'settings.round-robin-settings': { fields: ['timeMinutes', 'increment', 'controlType'] },
              },
            },
            teamSettings: true,
          },
          pagination: {
            page: options.page || 1,
            pageSize: limit,
          },
          sort: [...(options.includeOngoing ? ['tournamentStatus:desc'] : []), 'date:asc', 'time:asc', 'createdAt:asc'],
        },
        skipToken: true,
      });

      const entries = res.data?.data;
      const items = Array.isArray(entries?.data) ? entries.data : Array.isArray(entries) ? entries : [];
      const last = items.at(-1);
      if (options.includeOngoing && items.length === limit
        && last?.tournamentStatus === TournamentStatus.ONGOING) {
        const more = await this.getUpcomingEntries({ ...options, page: (options.page || 1) + 1 });
        return [...items, ...more];
      }
      return items;
    } catch (e) {
      log.error('getUpcomingPublic', 'Ошибка при получении ближайших турниров', e);
      if (options.throwOnError) throw e;
      return [];
    }
  }

  async getPublicLatestWinners(options = {}) {
    return this.#getPublicHomeList('/public/home/latest-winners', options);
  }

  async getPublicRatingMovers(options = {}) {
    return this.#getPublicHomeList('/public/home/rating-movers', options);
  }

  async getPublicTopPerformances(options = {}) {
    return this.#getPublicHomeList('/public/home/top-performances', options);
  }

  async #getPublicHomeList(path, options = {}) {
    try {
      const params = {};
      if (options?.limit != null) params.limit = options.limit;
      if (options?.periodDays != null) params.periodDays = options.periodDays;

      const res = await RootApiService.api.get(path, {
        params,
        skipToken: true,
      });

      return Array.isArray(res?.data?.data) ? res.data.data : [];
    } catch (e) {
      log.error('#getPublicHomeList', `Ошибка при получении ${path}`, e);
      if (options.throwOnError) throw e;
      return [];
    }
  }

  #getTodayDateString() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

}

export default new PublicTournamentService();
